// The savings calculator on /restaurants: what a few minutes off each check could be worth on a
// busy night. Pure math, no DOM, so the page and the tests (and the server, when it saves a lead)
// all use the same numbers. The math and example values are from the product spec. Extra turns
// only count when there's a wait, so the page calls it an estimate.

export type CalcKey = 'tables' | 'saved' | 'turn' | 'check' | 'nights';
export type CalcInputs = Record<CalcKey, number>;

/** The example values the fields start with. With these: 3.3 tables, $500 a night, $78,000 a year. */
export const CALC_DEFAULTS: CalcInputs = { tables: 30, saved: 10, turn: 90, check: 150, nights: 3 };

/** Field order on the page. Caps from the PRD: entries past them are read as the cap. */
export const CALC_KEYS: CalcKey[] = ['tables', 'saved', 'turn', 'check', 'nights'];
export const CALC_CAPS: Record<CalcKey, { min: number; max: number }> = {
  tables: { min: 1, max: 150 },
  saved: { min: 1, max: 30 },
  turn: { min: 30, max: 240 },
  check: { min: 1, max: 500 },
  nights: { min: 0, max: 7 },
};

export interface CalcResult { extraTables: number; night: number; week: number; month: number; year: number }
export type Calc =
  | { ok: true; inputs: CalcInputs; result: CalcResult }
  | { ok: false; reason: 'blank' };

/** "60", " $1,500 ", "50%" -> a number. Blank or not a number -> NaN. */
export function readNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').replace(/[\s$,%]/g, '');
  if (!s) return NaN;
  return /^[-+]?(\d+\.?\d*|\.\d+)$/.test(s) ? Number(s) : NaN;
}

/** A positive number, pulled into its cap. Blank, 0, negative or not a number -> null. */
export function capped(key: CalcKey, v: unknown): number | null {
  const n = readNumber(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  const c = CALC_CAPS[key];
  return Math.min(c.max, Math.max(c.min, n));
}

/**
 * Every field read and capped, then the v5 math: the minutes saved across the tables that split,
 * divided by the turn time, is the extra tables a night. Nothing is rounded here; rounding is only
 * for display. A blank or 0 turn time is a dash, never a divide by zero.
 */
export function calculate(raw: Partial<Record<CalcKey, unknown>>): Calc {
  const v = {} as CalcInputs;
  for (const k of CALC_KEYS) {
    const n = capped(k, raw[k]);
    if (n === null) return { ok: false, reason: 'blank' };
    v[k] = n;
  }
  const extraTables = (v.tables * v.saved) / v.turn;
  const night = extraTables * v.check;
  const week = night * v.nights;
  const year = week * 52;
  const month = year / 12;
  const result = { extraTables, night, week, month, year };
  if (!Object.values(result).every((x) => Number.isFinite(x) && x >= 0)) return { ok: false, reason: 'blank' };
  return { ok: true, inputs: v, result };
}

/**
 * The calculator's button, before and after it opens the email field, what shows once it's sent,
 * and the line under it. CONTACT is the button's only source: the page, the admin note and the
 * team email all read it, so renaming the button is this one line.
 */
export const CONTACT = 'Contact us';
export const SEND = 'Send';
export const SENT = "Thanks. We'll email you to set up a call.";
export const FOOTNOTE = "We'll only use this to set up a call.";

/** The fine print, and the line that takes its place when the estimate gets big. */
export const FINE_PRINT = "Example estimate. Extra turns only count when there's a wait.";
export const BIG_NUMBER = "That's a big number. It only holds if there's a wait all night, every busy night.";

/**
 * A sanity check on the estimate: minutes saved over a third of the turn time, or $1,000,000 or
 * more a year. Either one only holds with a line out the door all night.
 */
export function isBigNumber(c: Calc): boolean {
  return c.ok && (c.inputs.saved > c.inputs.turn / 3 || c.result.year >= 1_000_000);
}

/** The "About X more tables" line: only with a number, and not when it would read as 0 tables. */
export function showTablesLine(c: Calc): boolean {
  return c.ok && Math.round(c.result.extraTables * 10) / 10 >= 0.1;
}

export const DASH = '–';

/** $1,875: whole dollars with commas. Anything that isn't a real amount is a dash. */
export function fmtDollars(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return DASH;
  return '$' + Math.round(n).toLocaleString('en-US');
}

/** 12.5: one decimal place, and no ".0" on a whole number. */
export function fmtTables(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return DASH;
  return (Math.round(n * 10) / 10).toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/** What the page shows: four amounts and the tables line, or dashes. */
export function display(c: Calc): { night: string; week: string; month: string; year: string; tables: string } {
  if (!c.ok) return { night: DASH, week: DASH, month: DASH, year: DASH, tables: DASH };
  const r = c.result;
  return { night: fmtDollars(r.night), week: fmtDollars(r.week), month: fmtDollars(r.month), year: fmtDollars(r.year), tables: fmtTables(r.extraTables) };
}

/** The results saved with a lead: whole dollars and tables to one place, or null when there were dashes. */
export function savedResults(c: Calc): { extraTables: number; night: number; week: number; month: number; year: number } | null {
  if (!c.ok) return null;
  const r = c.result;
  return { extraTables: Math.round(r.extraTables * 10) / 10, night: Math.round(r.night), week: Math.round(r.week), month: Math.round(r.month), year: Math.round(r.year) };
}
